import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import '@/index.css';
import { Toaster } from 'sonner';

import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Dashboard from '@/pages/Dashboard';
import Profile from '@/pages/Profile';
import MealPlans from '@/pages/MealPlans';
import Health from '@/pages/Health';
import Recommendations from '@/pages/Recommendations';
import Social from '@/pages/Social';
import Producers from '@/pages/Producers';
import Education from '@/pages/Education';
import Subscription from '@/pages/Subscription';
import ResetPassword from '@/pages/ResetPassword';
import TwoFactor from '@/pages/TwoFactor';
import NutritionistPanel from '@/pages/NutritionistPanel';
import AuthCallback from '@/pages/AuthCallback';
import ShoppingList from '@/pages/ShoppingList';
import Supermarkets from '@/pages/Supermarkets';
import RecipesAdmin from '@/pages/RecipesAdmin';
import Recipes from '@/pages/Recipes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Allow if user passed from AuthCallback
  if (location.state?.user) return children;
  
  return user ? children : <Navigate to="/auth" />;
};

// Router wrapper to detect session_id in URL fragment
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <Auth />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/meal-plans" element={<ProtectedRoute><MealPlans /></ProtectedRoute>} />
      <Route path="/shopping-list" element={<ProtectedRoute><ShoppingList /></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute><Health /></ProtectedRoute>} />
      <Route path="/recommendations" element={<ProtectedRoute><Recommendations /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><Social /></ProtectedRoute>} />
      <Route path="/producers" element={<ProtectedRoute><Producers /></ProtectedRoute>} />
      <Route path="/education" element={<Education />} />
      <Route path="/recipes" element={<Recipes />} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/supermarkets" element={<ProtectedRoute><Supermarkets /></ProtectedRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/2fa" element={<ProtectedRoute><TwoFactor /></ProtectedRoute>} />
      <Route path="/nutritionist" element={<ProtectedRoute><NutritionistPanel /></ProtectedRoute>} />
      <Route path="/admin/recipes" element={<ProtectedRoute><RecipesAdmin /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F5F0]">
        <div className="text-[#1A4D2E] text-xl font-serif">Se încarcă...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, API }}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors toastOptions={{ className: 'sonner-toast' }} />
    </AuthContext.Provider>
  );
}

export default App;